import { AppDRUMConfig, IADRUM, IRUMMgrConfigData, IRUMStrategy, RUMEventData, RUMErrorPayload } from '@nab/x-types';
import { AppDRumUserDataProvider } from '@nab/nab-x-sdk-core';
import { loadScript } from '../../../utils/loadScript';

class AppDRUMStrategy implements IRUMStrategy {
    private currentUserDataProvider: AppDRumUserDataProvider;

    private configureUserEventInfo(initialValue?: any) {
        const defaultErrorFn = () => {};
        const errorFn = initialValue?.Error || defaultErrorFn;
        /* istanbul ignore next */
        return {
            ...initialValue,
            Error: () => {
                return {
                    ...errorFn(),
                    userData: {
                        ...errorFn()?.userData,
                        ...this.currentUserDataProvider?.getDataForError()
                    }
                };
            }
        };
    }

    private configureADRUM(configValue: AppDRUMConfig) {
        return {
            appKey: configValue.appKey,
            adrumExtUrlHttp: configValue.adrumExtUrlHttp,
            adrumExtUrlHttps: configValue.adrumExtUrlHttps,
            beaconUrlHttp: configValue.beaconUrlHttp,
            beaconUrlHttps: configValue.beaconUrlHttps,
            xd: configValue.xd,
            spa: configValue.spa,
            userEventInfo: this.configureUserEventInfo()
        };
    }

    constructor({ libraryUrl, configValue }: IRUMMgrConfigData<AppDRUMConfig>) {
        (async () => {
            try {
                if (!this.ADRUM && libraryUrl && configValue && Object.keys(configValue).length > 0) {
                    if (configValue.appKey) {
                        window['adrum-start-time'] = new Date().getTime();
                        window['adrum-config'] = this.configureADRUM(configValue);
                        await loadScript(libraryUrl, document.head);
                    } else {
                        // eslint-disable-next-line no-console
                        console.error('AppDRUMStrategy: Failed to initialised the strategy!', new Error('appKey is not provided'));
                    }
                } else {
                    const rumConfig = window['adrum-config'];
                    if (rumConfig && Object.keys(rumConfig).length > 0) {
                        rumConfig.userEventInfo = this.configureUserEventInfo(rumConfig.userEventInfo);
                    } else {
                        window['adrum-config'] = this.configureADRUM(configValue);
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('AppDRUMStrategy: Failed to initialised the strategy!', e);
            }
        })();
    }

    private initialiseWarning() {
        // eslint-disable-next-line no-console
        console.warn('AppDRUMStrategy: ADRUM is not initialised!');
    }

    private get ADRUM(): IADRUM {
        return (window as any).ADRUM as IADRUM;
    }

    /**
     * The emitEvent function is a generic collection of some of the functions available when using AppD RUM, this provides a single entry point for the user object to map this to.
     * @param eventData The type of event you would like to track / send off to the AppD Rum instance
     * @param userDataProvider When providing the optional userDataProvider this allows an app to set custom data to the event it may be firing
     */
    public emitEvent(eventData: RUMEventData, userDataProvider?: AppDRumUserDataProvider): void {
        this.currentUserDataProvider = userDataProvider;
        switch (eventData.eventType) {
            case 'error':
                this.reportError(eventData.payload);
                break;
            case 'virtual-page-begin':
                this.markVirtualPageBegin(eventData.payload.pageName, eventData.payload.manuallyMarkVPEnd);
                break;
            case 'virtual-page-end':
                this.markVirtualPageEnd();
                break;
            default:
                // eslint-disable-next-line no-console
                console.warn('AppDRUMStrategy: Unknown event emitted!');
        }

        // reset the userData provider after consumed it as it is to be used only once during one event
        setTimeout(() => {
            this.currentUserDataProvider = null;
        });
    }

    private reportError(error: RUMErrorPayload) {
        if (!this.ADRUM) {
            this.initialiseWarning();
        } else {
            const rumError = new this.ADRUM.events.Error();
            rumError.msg(error.msg);
            rumError.line(error.line);
            this.ADRUM.report(rumError);
        }
    }

    private markVirtualPageBegin(pageName: string, manuallyMarkVPEnd: boolean = true) {
        if (!this.ADRUM) {
            this.initialiseWarning();
        } else {
            this.ADRUM.markVirtualPageBegin(pageName, manuallyMarkVPEnd);
        }
    }

    private markVirtualPageEnd() {
        if (!this.ADRUM) {
            this.initialiseWarning();
        } else {
            this.ADRUM.markVirtualPageEnd();
        }
    }
}

export { AppDRUMStrategy };
